//This page will implement the facial liveness UI from AWS Amplify. // To be used by web app. 


import React, { useEffect, useState } from 'react';
import axios from 'axios';

import { Amplify } from 'aws-amplify';
import { ThemeProvider, Loader } from '@aws-amplify/ui-react';
import { FaceLivenessDetector } from '@aws-amplify/ui-react-liveness';
import '@aws-amplify/ui-react/styles.css';
import awsexports from '../aws-exports';

import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

Amplify.configure(awsexports);

// onLivenessResult is a function that will be passed from the parent component.
// It takes the result of the liveness test and the session id as arguments.
// It is a callback function, a parent component must implement it to get the result of the liveness test.
function FacialLiveness({ onLivenessResult, language = 'Eng' }) {
  const [isLive, setIsLive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionId, setSessionId] = useState(null);

   // Map 'Eng'/'Spa' (and en/es) to the Amplify codes
  const normalizeLang = (l) => {
    const map = { Eng: 'en', Spa: 'es', en: 'en', es: 'es' };
    return map[l] || 'en';
  };

  const langCode = normalizeLang(language);

  // i18n dictionary for the Liveness UI
  const livenessDictionary = {
    en: null, // use Amplify defaults
    es: {
      // Instructions
      goodFitCaptionText: 'Buen ajuste',
      goodFitAltText: 'Ilustración de un rostro encajando dentro de un óvalo.',
      tooFarCaptionText: 'Demasiado lejos',
      tooFarAltText:
        'Ilustración de un rostro dentro de un óvalo; hay un espacio entre el perímetro de la cara y los límites del óvalo.',
      startScreenBeginCheckText: 'Comenzar verificación de video',
      // Hints
      hintCenterFaceText: 'Centra tu cara',
      hintCenterFaceInstructionText:
        'Antes de comenzar, centra tu cara a la cámara en la parte superior. Acércate hasta llenar el óvalo y mantente quieto hasta completar la verificación.',
      hintFaceOffCenterText: 'La cara no está en el óvalo, céntrala.',
      hintMoveFaceFrontOfCameraText: 'Mueve la cara frente a la cámara',
      hintTooManyFacesText: 'Asegúrate de que solo haya una cara',
      hintFaceDetectedText: 'Cara detectada',
      hintCanNotIdentifyText: 'Mueve la cara frente a la cámara',
      hintTooCloseText: 'Aléjate',
      hintTooFarText: 'Acércate',
      hintConnectingText: 'Conectando...',
      hintVerifyingText: 'Verificando...',
      hintCheckCompleteText: 'Verificación completa',
      hintIlluminationTooBrightText: 'Muévete a un lugar con menos luz',
      hintIlluminationTooDarkText: 'Muévete a un lugar más iluminado',
      hintIlluminationNormalText: 'Iluminación normal',
      hintHoldFaceForFreshnessText: 'Mantente quieto',
      hintMatchIndicatorText: '50% completado. Sigue acercándote.',
      // Camera
      cameraMinSpecificationsHeadingText:
        'La cámara no cumple con las especificaciones mínimas',
      cameraMinSpecificationsMessageText:
        'La cámara debe soportar al menos 320×240 de resolución y 15 fps.',
      cameraNotFoundHeadingText: 'No se puede acceder a la cámara',
      cameraNotFoundMessageText:
        'Verifica que haya una cámara conectada y que ninguna otra aplicación la esté usando. Revisa permisos del navegador, cierra otras apps y vuelve a intentar.',
      waitingCameraPermissionText:
        'Esperando a que permitas el uso de la cámara.',
      a11yVideoLabelText: 'Cámara para verificación de liveness',
      retryCameraPermissionsText: 'Reintentar',
      cancelLivenessCheckText: 'Cancelar verificación',

      // Photosensitivity warning
      photosensitivityWarningHeadingText: 'Advertencia de fotosensibilidad',
      photosensitivityWarningBodyText:
        'Esta verificación muestra luces de colores. Ten cuidado si eres fotosensible.',
      photosensitivityWarningInfoText:
        'Algunas personas pueden experimentar convulsiones epilépticas al exponerse a luces de colores. Ten precaución si tú o alguien de tu familia tiene esta condición.',
      photosensitivityWarningLabelText:
        'Más información sobre fotosensibilidad',

      // Error texts (optional but recommended)
      errorLabelText: 'Error',
      connectionTimeoutHeaderText: 'Tiempo de conexión agotado',
      connectionTimeoutMessageText: 'La conexión se agotó.',
      timeoutHeaderText: 'Tiempo agotado',
      timeoutMessageText:
        'La cara no encajó en el óvalo a tiempo. Intenta de nuevo y llena por completo el óvalo.',
      faceDistanceHeaderText: 'Movimiento hacia adelante detectado',
      faceDistanceMessageText: 'Evita acercarte durante la conexión.',
      multipleFacesHeaderText: 'Se detectaron múltiples caras',
      multipleFacesMessageText:
        'Asegúrate de que solo una cara esté frente a la cámara.',
      clientHeaderText: 'Error del cliente',
      clientMessageText: 'La verificación falló por un problema del cliente.',
      serverHeaderText: 'Problema del servidor',
      serverMessageText:
        'No se puede completar la verificación por un problema del servidor.',
      landscapeHeaderText: 'Orientación horizontal no compatible',
      landscapeMessageText:
        'Gira tu dispositivo a orientación vertical (retrato).',
      portraitMessageText:
        'Mantén el dispositivo en orientación vertical durante la verificación.',
      tryAgainText: 'Intentar de nuevo',
      recordingIndicatorText: 'Rec',
    },
  };

 


  useEffect(() => {
    // Fetch the liveness session ID from the backend.
    const createSessionWithRetry = async (attempts = 3) => {
      let lastErr;
      for (let i = 0; i < attempts; i++) {
        try {
          const { data } = await axios.get(process.env.REACT_APP_LIVENESS_CreateSession, { timeout: 8000 });
          return data;
        } catch (e) {
          lastErr = e;
          await new Promise(r => setTimeout(r, 500 * (i + 1)));
        }
      }
      throw lastErr;
    };

    (async () => {
      try {
        const data = await createSessionWithRetry(3);
        setSessionId(data.livenessSessionId);
      } catch (error) {
        const msg = error?.response?.data?.message || error?.message || 'Create session failed';
        console.error('[Liveness createSession] error', error);
        toast.error(msg);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Function to retry liveness test
  const handleRetry = async () => {
    setIsLive(null);
    setLoading(true);
    // Fetch a new liveness session with retry
    const createSessionWithRetry = async (attempts = 3) => {
      let lastErr;
      for (let i = 0; i < attempts; i++) {
        try {
          const { data } = await axios.get(process.env.REACT_APP_LIVENESS_CreateSession, { timeout: 8000 });
          return data;
        } catch (e) {
          lastErr = e;
          await new Promise(r => setTimeout(r, 500 * (i + 1)));
        }
      }
      throw lastErr;
    };

    try {
      const data = await createSessionWithRetry(3);
      setSessionId(data.livenessSessionId);
    } catch (error) {
      const msg = error?.response?.data?.message || error?.message || 'Create session failed';
      console.error('[Liveness retry createSession] error', error);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isLive === false) {
      toast.error('Not human, try again.');
    } else if (isLive === true) {
      toast.success('You are a real person.');
    }
  }, [isLive]);




  // Get the analysis data from the backend. THE LIVENESS ANALYSIS IS DONE IN THE BACKEND.
  const handleAnalysisComplete = async () => {
    // Send a POST request to the backend to get the analysis results for the liveness session.
    // The sessionId is sent in the request body to identify the session.
    // The backend will return the analysis results, which include whether the liveness check was successful.
    const response = await fetch(process.env.REACT_APP_LIVENESS_GET_SESSION_RESULTS_API, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ sessionId: sessionId }),
    });

    // Note: The isLive flag is not returned from the GetFaceLivenessSession API
    // This should be returned from your backend based on the score that you
    // get in response. Based on the return value of your API you can determine what to render next.
    // Any next steps from an authorization perspective should happen in your backend and you should not rely
    // on this value for any auth related decisions.
    const data = await response.json();

    if (data.isLive === true) {
      setIsLive(true);
      onLivenessResult(true, sessionId); // Pass the result to the parent component, the parent will handle the next steps. 
    } else {
      setIsLive(false);
      onLivenessResult(false, sessionId); // Notify parent of failure, so it can handle it accordingly.
    }
  };

  return (
    //Previously there was a button to go back to the dashboard, but is was removed. 
    // with an automatic redirect to the dashboard after the liveness test completed. 
    
    <ThemeProvider>
      {loading ? (
        <Loader />
      ) : (
        <>
          {isLive === null ? (
            <FaceLivenessDetector
              sessionId={sessionId}
              region="us-east-1"
              onAnalysisComplete={handleAnalysisComplete}
              onError={(error) => {
                const name = error?.name || error?.code || 'UnknownError';
                const message =
                  error?.message ||
                  error?.reason ||
                  (typeof error === 'string' ? error : JSON.stringify(error));
                console.error('[Liveness onError]', error);
                toast.error(`${name}: ${message}`);
              }}
              disableStartScreen={true}
              displayText={livenessDictionary[langCode]}
              
            />
          ) : isLive ? (
            <p className="text-body text-active text-center">Liveness Test Passed</p>
          ) : (
            <>
              <p className="text-body text-critical text-center">Liveness Test Failed</p>
              <button
                onClick={handleRetry}
                className="bg-ink text-white px-4 py-2 rounded-lg text-label font-medium hover:bg-ink-2 transition"
              >
                Retry
              </button>
            </>
          )}
        </>
      )}
    </ThemeProvider>
  );
}

export default FacialLiveness;
